import { ErrorOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import { Button, CircularProgress, InputAdornment, InputBase } from "@mui/material";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Lock, ArrowLeft, CheckCircle } from "lucide-react";
import Logo from "../reusables/Logo";
import { setTokens } from "../../utils/authTokens";
import { fetchAndSetOrganization } from "../../utils/organizationService";

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { data } = useParams();
  const [user, setUser] = useState(null);
  const [tokenError, setTokenError] = useState(false);
  const navigate = useNavigate();

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  function decodeToken(token) {
    try {
      const decoded = jwtDecode(token);
      return decoded;
    } catch (error) {
      // Log when reset token is invalid or expired
      console.error("Invalid token:", error);
      return null;
    }
  }

  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const resetPassword = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg(null);

    if (user.password !== user.confirm_password) {
      setErrorMsg("Password and Confirm must match!");
      return;
    }

    if (!user.password || user.password.length < 1) {
      setErrorMsg("Please enter a password.");
      return;
    }

    setLoader(true);
    try {
      let user_data = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "user/change-password",
        { reset_token: data, newPassword: user.password }
      );

      localStorage.setItem("user", JSON.stringify(user_data.data.user));

      // Auth token for subsequent API calls (get-organization). Prefer tokens from
      // change-password response; otherwise log in with email + new password.
      let access_token = user_data?.data?.access_token;
      if (!access_token && (user.email || user_data?.data?.user?.email?.S)) {
        const loginEmail = user.email || user_data?.data?.user?.email?.S;
        const loginRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "get-user",
          { email: loginEmail, password: user.password }
        );
        if (loginRes.data?.access_token) {
          setTokens({
            access_token: loginRes.data.access_token,
            refresh_token: loginRes.data.refresh_token,
            expires_in: loginRes.data.expires_in,
          });
        }
      } else if (access_token) {
        setTokens({
          access_token: user_data.data.access_token,
          refresh_token: user_data.data.refresh_token,
          expires_in: user_data.data.expires_in,
        });
      }

      let org_id =
        user_data?.data?.user?.relatedOrgs?.L[0]?.M?.org_id?.S || null;
      if (!org_id) {
        console.info("[Auth] Redirecting to create-organization: reset password succeeded but user has no organizations.");
        navigate("/create-organization");
      } else {
        await fetchAndSetOrganization(org_id);
      }

      setIsSuccess(true);
      setTimeout(() => {
        navigate("/dashboard/");
      }, 3000);
    } catch (error) {
      // Log for production diagnostics when change-password fails
      console.error("ResetPassword: change password failed", error.response?.data || error.message || error);
      if (error.response?.data?.message) {
        setErrorMsg(error.response.data.message);
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!data) {
      setTokenError(true);
      return;
    }
    const decodedData = decodeToken(data);
    if (!decodedData) {
      setTokenError(true);
      return;
    }
    // Client-side exp check for immediate UX; backend still validates on submit
    if (decodedData.exp != null && Date.now() / 1000 > decodedData.exp) {
      setTokenError(true);
      return;
    }
    setUser({ ...decodedData, password: "", confirm_password: "" });
  }, [data]);

  // Invalid or expired token
  if (tokenError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Logo size="md" />
          </div>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <ErrorOutline className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Invalid or expired link
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              This password reset link is invalid or has expired. Please request
              a new one.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center w-full py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#605BFF] hover:bg-[#4f4adf]"
            >
              Request new reset link
            </Link>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 flex items-center justify-center text-sm text-[#605BFF] hover:text-[#4f4adf] w-full"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Logo size="md" />
          </div>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Password updated
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Your password has been reset successfully. Redirecting you to the
              dashboard...
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center w-full py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#605BFF] hover:bg-[#4f4adf]"
            >
              Go to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading (token decoding)
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12">
        <div className="flex justify-center">
          <Logo size="md" />
        </div>
        <CircularProgress sx={{ color: "#605BFF", mt: 4 }} />
      </div>
    );
  }

  // Form
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo size="md" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your new password below.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10">
          {err_msg && (
            <div className="text-center text-red-500 text-sm mt-2 flex justify-center items-center font-semibold mb-4">
              <ErrorOutline sx={{ fontSize: "16px", mr: "4px" }} /> {err_msg}
            </div>
          )}

          <form
            onSubmit={resetPassword}
            className="space-y-6"
          >
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                New password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <InputBase
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={user.password || ""}
                  onChange={(e) =>
                    setUser({ ...user, password: e.target.value })
                  }
                  disabled={loader}
                  placeholder="Enter new password"
                  endAdornment={
                    <InputAdornment
                      position="end"
                      onClick={handleTogglePassword}
                      className="cursor-pointer"
                      sx={{ mr: 1 }}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 20 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 20 }} />
                      )}
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #D1D5DB",
                    width: "100%",
                    pl: "40px",
                    pr: "44px",
                    py: "8px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    "&:focus": {
                      outline: "none",
                      ring: "2px",
                      ringColor: "#605BFF",
                      borderColor: "#605BFF",
                    },
                    "&:disabled": {
                      bgcolor: "#F9FAFB",
                      color: "#6B7280",
                    },
                  }}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="confirm_password"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <InputBase
                  id="confirm_password"
                  name="confirm_password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={user.confirm_password || ""}
                  onChange={(e) =>
                    setUser({ ...user, confirm_password: e.target.value })
                  }
                  disabled={loader}
                  placeholder="Confirm new password"
                  endAdornment={
                    <InputAdornment
                      position="end"
                      onClick={handleTogglePassword}
                      className="cursor-pointer"
                      sx={{ mr: 1 }}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 20 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 20 }} />
                      )}
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #D1D5DB",
                    width: "100%",
                    pl: "40px",
                    pr: "44px",
                    py: "8px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    "&:focus": {
                      outline: "none",
                      ring: "2px",
                      ringColor: "#605BFF",
                      borderColor: "#605BFF",
                    },
                    "&:disabled": {
                      bgcolor: "#F9FAFB",
                      color: "#6B7280",
                    },
                  }}
                />
              </div>
            </div>

            <div>
              <Button
                type="submit"
                fullWidth
                disabled={
                  loader ||
                  !user.password ||
                  !user.confirm_password ||
                  user.password !== user.confirm_password
                }
                variant="contained"
                  color="primary"
                  sx={{
                  py: "10px",
                  px: "16px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    bgcolor: "#4f4adf",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}
              >
                {loader ? (
                  <CircularProgress size="1rem" sx={{ color: "white" }} />
                ) : (
                  "Reset password"
                )}
              </Button>
            </div>
          </form>

          <div className="mt-6">
            <button
              onClick={() => navigate("/login")}
              className="flex items-center justify-center text-sm text-[#605BFF] hover:text-[#4f4adf] w-full"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
